import React, { useEffect, useMemo, useRef, useState } from 'react';

import { DateTime } from 'luxon';

import { Box, COLORS, Text } from '@clutter/clean';

import {
  AvailabilityFragment,
  CouponFragment,
  OrderServiceTypeEnum,
  OrderSubtypeEnum,
  OrderTypeEnum,
} from '@graphql/platform';

import { StorageCheckoutStepProps } from '@root/components/checkout/types';
import { EventSchema } from '@root/resources/wt/types';

import { Calendar } from '@shared/calendar';
import {
  FROM_ISO_OPTIONS,
  hashDateTime,
} from '@shared/calendar/calendar_utils';
import { Currency } from '@shared/currency';

import { useFilteredAvailabilities } from '@utils/availability';
import { useStabilizedFunction } from '@utils/hooks';
import { useCalendarCouponTest } from '@utils/hooks/ab_testing';
import { useStorageAvailabilities } from '@utils/hooks/availabilities';
import { useAppointmentFunnelEvents } from '@utils/hooks/funnel_events/use_appointment_time_funnel_events';
import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';
import { usePricingSetForZIP } from '@utils/hooks/pricing';
import { extractPlan } from '@utils/pricing/pricing_set';
import {
  EventName,
  createThirdPartyConversionEvent,
} from '@utils/third_party_conversion_events';

import { ArrivalWindowSelector } from '../shared/appointment/arrival_window_selector';
import { LaborAdjustmentBanner } from '../shared/appointment/labor_adjustment_banner';

export const Appointment: React.FC<StorageCheckoutStepProps> = ({
  onChange,
  scrollToStep,
  enabled,
  values: { commitment, datePreferred, dateScheduled, planSize, zip },
}) => {
  const arrivalWindowSelectorRef = useRef<HTMLDivElement>(null);
  const [availableDays, setAvailableDays] = useState<Set<string> | undefined>();
  const [couponDays, setCouponDays] = useState<
    Record<string, CouponFragment> | undefined
  >();
  const [defaultCoupon, setDefaultCoupon] = useState<
    CouponFragment | undefined
  >();
  const pricingSet = usePricingSetForZIP(zip);
  const dateInteractionRef = useRef(datePreferred !== undefined);
  const trackFunnelEvent = useTrackFunnelEvents();

  useEffect(() => {
    if (enabled) {
      trackFunnelEvent({
        schema: EventSchema.WWW__PickupPriceViewed,
        action: 'display',
        metadata: {},
      });
    }
  }, [enabled, trackFunnelEvent]);

  useEffect(() => {
    if (!enabled || !datePreferred) {
      dateInteractionRef.current = false;
    }
  }, [enabled, datePreferred]);

  const rateGroupName = commitment;

  const expectedPlanID = extractPlan(
    rateGroupName,
    planSize,
    pricingSet ?? undefined,
  )?.id;

  const { availabilities, fetchMore, from, till, loading, fetched } =
    useStorageAvailabilities({
      expectedPlanID,
      orderSubtype: OrderSubtypeEnum.Onboarding,
      orderType: OrderTypeEnum.Pickup,
      serviceType: OrderServiceTypeEnum.FullService,
      zip: zip!,
      skip: !zip,
    });

  const filteredAvailabilities = useFilteredAvailabilities(
    availabilities,
    datePreferred,
  );

  useEffect(() => {
    if (!availabilities) {
      return;
    }
    const selectables = new Set<string>();
    const couponSelectables: Record<string, CouponFragment> = {};
    for (const availability of availabilities) {
      const selectable = DateTime.fromISO(
        availability.fromTime,
        FROM_ISO_OPTIONS,
      );
      selectables.add(hashDateTime(selectable));
      if (availability.coupon) {
        couponSelectables[hashDateTime(selectable)] = availability.coupon;
      }
    }
    setAvailableDays(selectables);
    setCouponDays(couponSelectables);
    setDefaultCoupon(Object.values(couponSelectables)[0]);
  }, [availabilities]);

  // Auto-select the first time slot, which will be the all-day SLA time slot
  useEffect(() => {
    if (filteredAvailabilities && filteredAvailabilities.length > 0) {
      onTimeChange(filteredAvailabilities[0] as AvailabilityFragment, false);
    }
  }, [filteredAvailabilities]);

  const currentDay = DateTime.local().startOf('day');

  const isUnavailable = useStabilizedFunction(
    (value: DateTime, availabilitySet: Set<string> | undefined) =>
      !availabilitySet?.has(hashDateTime(value)),
  );

  const calendarCouponEligible = useCalendarCouponTest();

  const getCoupon = useStabilizedFunction(
    (
      value: DateTime,
      availabilitySet: Record<string, CouponFragment> | undefined,
    ) =>
      calendarCouponEligible
        ? availabilitySet?.[hashDateTime(value)]
        : undefined,
  );

  const { trackAppointmentDateCompleted, trackArrivalWindowCompleted } =
    useAppointmentFunnelEvents({
      enabled,
      from,
      till,
      originZip: zip ?? '',
      appointmentDatesShown: fetched,
      arrivalWindowCount: filteredAvailabilities?.length,
      currentDay,
      availableDays,
      isUnavailable,
    });

  const selectedAvailability = useMemo(() => {
    if (!datePreferred) {
      return;
    }

    return filteredAvailabilities?.find((a) =>
      datePreferred.hasSame(DateTime.fromISO(a.fromTime), 'day'),
    );
  }, [datePreferred, filteredAvailabilities]);

  const isPastDay = (value: DateTime) => value <= currentDay;

  const onDateChange = (date: DateTime) => {
    if (availableDays?.has(hashDateTime(date))) {
      trackFunnelEvent({
        schema: EventSchema.WWW__PickupPriceCompleted,
        action: 'click',
        metadata: {},
      });
      if (calendarCouponEligible) {
        onChange('coupon', getCoupon(date, couponDays));
      }
      trackAppointmentDateCompleted(date.toISO());
      onChange('datePreferred', date);
      arrivalWindowSelectorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
      createThirdPartyConversionEvent(EventName.DATE_PREFERRED, {
        date_preferred: date?.toISO(),
        zip_code: zip,
      });
      onChange('dateScheduled', undefined);
    }
  };

  const onTimeChange = (
    availability: AvailabilityFragment,
    autoScroll: boolean,
  ) => {
    trackArrivalWindowCompleted(availability.formatted);
    onChange('dateScheduled', availability);

    if (autoScroll) {
      scrollToStep('next');
    }
    createThirdPartyConversionEvent(EventName.AVAILABILITY, {
      date_scheduled: availability?.fromTime,
    });
  };

  return (
    <>
      <Box margin={['64px 0 0', '72px 0 0']}>
        <Text.Title size="small" color={COLORS.tealDark}>
          When do you want us to pick up your items?
        </Text.Title>
        <Box margin="4px 0 32px">
          <Text.Body color={COLORS.storm}>
            Availability fills up fast. Secure your spot now. You can always
            change your appointment date later in your account portal!
          </Text.Body>
        </Box>
        <Calendar
          from={from}
          till={till}
          fetchMore={loading ? undefined : fetchMore}
          selected={(value) =>
            !!datePreferred && datePreferred.hasSame(value, 'day')
          }
          pastDay={isPastDay}
          unavailable={(value) => isUnavailable(value, availableDays)}
          coupon={(value) => getCoupon(value, couponDays)}
          couponPromoDescription={
            !!defaultCoupon && calendarCouponEligible ? (
              <>
                {defaultCoupon.amount ? (
                  <Currency amount={defaultCoupon.amount} />
                ) : (
                  `${defaultCoupon.percent * 100}%`
                )}{' '}
                off first month of storage
              </>
            ) : undefined
          }
          onSelect={onDateChange}
        />
        {datePreferred && !filteredAvailabilities?.length && (
          <Box textAlign="center">
            <Text.Body>
              We’re fully booked for that date! Please try another date.
            </Text.Body>
          </Box>
        )}
        {datePreferred &&
          !!filteredAvailabilities?.length &&
          calendarCouponEligible &&
          selectedAvailability?.coupon && (
            <Box margin="0 0 24px">
              <LaborAdjustmentBanner
                date={datePreferred}
                coupon={selectedAvailability.coupon}
              />
            </Box>
          )}
        <Box
          ref={arrivalWindowSelectorRef}
          style={
            datePreferred && !!filteredAvailabilities?.length
              ? undefined
              : { visibility: 'hidden' }
          }
        >
          <ArrivalWindowSelector
            selectedAvailability={dateScheduled}
            availabilities={filteredAvailabilities ?? []}
            onChange={onTimeChange}
          />
        </Box>
      </Box>
    </>
  );
};
